import { Fragment, useCallback, useEffect, useState } from 'react'
import { api, type ClientStat, type DomainHit } from '../adminApi'
import { fmtBytes, fmtAgoUnix, fmtDay, daysLeft } from '../format'

type Sort = 'traffic' | 'online' | 'name' | 'expiry'

function ExpiryCell({ s }: { s: string | null }) {
  const d = daysLeft(s)
  if (d === null) return <span className="muted">∞</span>
  const color = d < 0 ? 'var(--err)' : d < 7 ? 'var(--warn)' : 'var(--muted)'
  return <span style={{ color, whiteSpace: 'nowrap' }}>{d < 0 ? 'истёк' : fmtDay(s)}</span>
}

function DomainList({ id }: { id: string }) {
  const [rows, setRows] = useState<DomainHit[] | null>(null)
  useEffect(() => {
    let alive = true
    api.clientDomains(id).then((r) => { if (alive) setRows(r) }).catch(() => { if (alive) setRows([]) })
    return () => { alive = false }
  }, [id])
  if (rows === null) return <div className="muted" style={{ fontSize: 12.5, padding: '8px 0' }}><span className="spin" /></div>
  if (rows.length === 0) return <div className="muted" style={{ fontSize: 12.5, padding: '8px 0' }}>Нет данных по сайтам за текущий лог.</div>
  const max = Math.max(...rows.map((r) => r.hits))
  return (
    <div style={{ display: 'grid', gap: 4, padding: '8px 0 4px' }}>
      <div className="muted" style={{ fontSize: 11.5, marginBottom: 2 }}>Топ сайтов этого клиента:</div>
      {rows.map((r) => (
        <div key={r.domain} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
          <span className="mono" style={{ flex: '1 1 0', minWidth: 0, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.domain}</span>
          <span style={{ flex: 1, height: 6, background: 'var(--line)', borderRadius: 4, overflow: 'hidden' }}>
            <span style={{ display: 'block', height: '100%', width: `${(r.hits / max) * 100}%`, background: 'var(--accent)', opacity: 0.55 }} />
          </span>
          <span className="muted" style={{ minWidth: 44, textAlign: 'right' }}>{r.hits}</span>
        </div>
      ))}
    </div>
  )
}

export function Clients() {
  const [rows, setRows] = useState<ClientStat[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [sort, setSort] = useState<Sort>('traffic')
  const [open, setOpen] = useState<string | null>(null)

  const load = useCallback(async () => {
    try { setRows(await api.clientsStats()); setErr(null) }
    catch { setErr('Не удалось загрузить клиентов.') }
    finally { setLoading(false) }
  }, [])

  useEffect(() => {
    load()
    const id = window.setInterval(load, 60000)
    return () => clearInterval(id)
  }, [load])

  if (loading) return <div className="empty"><span className="spin" /></div>
  if (err) return <div className="alert err">{err}</div>

  const s = q.trim().toLowerCase()
  let list = rows.filter((r) => !s || r.label.toLowerCase().includes(s))
  list = [...list].sort((a, b) => {
    if (sort === 'traffic') return (b.up + b.down) - (a.up + a.down)
    if (sort === 'online') return (Number(b.online) - Number(a.online)) || (b.lastOnline - a.lastOnline)
    if (sort === 'expiry') return (new Date(a.expiresAt || '2999-01-01').getTime()) - (new Date(b.expiresAt || '2999-01-01').getTime())
    return a.label.localeCompare(b.label, 'ru')
  })

  const onlineCount = rows.filter((r) => r.online).length

  return (
    <>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <input className="input" placeholder="Поиск по имени…" value={q} onChange={(e) => setQ(e.target.value)} style={{ flex: 1, minWidth: 180, marginBottom: 0 }} />
        <div style={{ display: 'flex', gap: 6 }}>
          {([['traffic', 'по трафику'], ['online', 'по онлайну'], ['expiry', 'по сроку'], ['name', 'по имени']] as [Sort, string][]).map(([k, lbl]) => (
            <button key={k} className={`btn sm ${sort === k ? 'primary' : ''}`} onClick={() => setSort(k)}>{lbl}</button>
          ))}
        </div>
      </div>
      <div className="muted" style={{ fontSize: 12.5, marginBottom: 10 }}>{rows.length} клиентов · <span style={{ color: 'var(--ok)' }}>{onlineCount} онлайн</span> · клик по строке — сайты клиента</div>

      <div className="card panel" style={{ padding: 0, overflowX: 'auto' }}>
        <table className="tbl" style={{ minWidth: 340 }}>
          <thead>
            <tr className="muted" style={{ textAlign: 'left', fontSize: 11 }}>
              <th style={{ padding: '10px 12px', fontWeight: 500 }}>Клиент</th>
              <th style={{ padding: '10px 8px', fontWeight: 500 }}>Онлайн</th>
              <th className="sm-hide" style={{ padding: '10px 8px', fontWeight: 500, textAlign: 'right' }}>↓</th>
              <th className="sm-hide" style={{ padding: '10px 8px', fontWeight: 500, textAlign: 'right' }}>↑</th>
              <th style={{ padding: '10px 8px', fontWeight: 500, textAlign: 'right' }}>Трафик</th>
              <th className="sm-hide" style={{ padding: '10px 8px', fontWeight: 500 }}>Активность</th>
              <th style={{ padding: '10px 12px', fontWeight: 500 }}>Срок</th>
            </tr>
          </thead>
          <tbody>
            {list.map((r) => {
              const id = r.id
              const canDrill = true
              const isOpen = open === id
              const pct = r.trafficLimitGb > 0 ? Math.round((r.total / (r.trafficLimitGb * 1073741824)) * 100) : null
              return (
                <Fragment key={id}>
                  <tr
                    onClick={() => canDrill && setOpen(isOpen ? null : id)}
                    style={{ borderTop: '1px solid var(--line)', cursor: canDrill ? 'pointer' : 'default', background: isOpen ? 'color-mix(in srgb, var(--accent) 5%, transparent)' : undefined }}
                  >
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ fontWeight: 600 }}>{r.label}</span>
                      {!r.hasTunnel && <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>бот</span>}
                      {r.status === 'revoked' && <span className="badge revoked" style={{ marginLeft: 6 }}>снят</span>}
                    </td>
                    <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>
                      {r.online
                        ? <span style={{ color: 'var(--ok)' }}>🟢 {r.devices} {r.devices === 1 ? 'устр.' : 'устр.'}</span>
                        : <span className="muted">⚪</span>}
                    </td>
                    <td className="mono sm-hide" style={{ padding: '10px 8px', textAlign: 'right' }}>{fmtBytes(r.down)}</td>
                    <td className="mono muted sm-hide" style={{ padding: '10px 8px', textAlign: 'right' }}>{fmtBytes(r.up)}</td>
                    <td style={{ padding: '10px 8px', textAlign: 'right' }} className="mono">
                      {fmtBytes(r.total)}
                      <span className="muted only-sm" style={{ fontSize: 10.5, display: 'block' }}>↓{fmtBytes(r.down)} · ↑{fmtBytes(r.up)}</span>
                      {pct !== null && <span className="muted" style={{ fontSize: 11, display: 'block' }}>{pct}% лимита</span>}
                    </td>
                    <td className="muted sm-hide" style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>{r.online ? 'сейчас' : fmtAgoUnix(r.lastOnline)}</td>
                    <td style={{ padding: '10px 12px' }}><ExpiryCell s={r.expiresAt} /></td>
                  </tr>
                  {isOpen && (
                    <tr style={{ background: 'color-mix(in srgb, var(--accent) 5%, transparent)' }}>
                      <td colSpan={7} style={{ padding: '0 12px 8px' }}><DomainList id={r.id} /></td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
            {list.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 20 }} className="muted">Ничего не найдено.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}
